/**
 * Per-tab CrossTabLock state.
 *
 * Holds at most one active lock at a time (one station per tab — CLAUDE.md §1).
 * The actual Dexie + heartbeat machinery lives in `src/persistence/tabLock/`;
 * this store is a thin Zustand-shaped wrapper so React components can subscribe
 * to "do I hold the lock" / "is another tab holding it" without poking Dexie
 * directly.
 */
import { create } from 'zustand';
import { db } from '@/persistence/dexie/db';
import { TabLockRepositoryDexie } from '@/persistence/dexie/repositories';
import type { TabLockRow } from '@/persistence/ports';
import {
  CrossTabLock,
  DEFAULT_HEARTBEAT_INTERVAL_MS,
  DEFAULT_STALE_THRESHOLD_MS,
  LockReaper,
  TabBroadcast,
  type TabBroadcastEvent,
} from '@/persistence/tabLock';
import { getOrCreateTabId } from '@/lib/tabId';

export type LockStatus = 'idle' | 'acquiring' | 'held' | 'foreign';

export interface TabLockStoreState {
  readonly tabId: string;
  readonly cpid: string;
  readonly wsUrl: string;
  readonly status: LockStatus;
  readonly holder: TabLockRow | null;
}

export interface TabLockStoreActions {
  tryAcquire(cpid: string, wsUrl: string): Promise<{ acquired: boolean }>;
  release(): Promise<void>;
  /** Poll for lock state changes — called by StationLockedPage. */
  refresh(): Promise<void>;
}

export type TabLockStore = TabLockStoreState & TabLockStoreActions;

interface Internal {
  lock: CrossTabLock | null;
  reaper: LockReaper | null;
  broadcast: TabBroadcast | null;
}

const internals = new WeakMap<object, Internal>();

export const useTabLockStore = create<TabLockStore>((set, get) => {
  const tag: object = {};
  internals.set(tag, { lock: null, reaper: null, broadcast: null });

  // The reaper runs from this tab too — any tab can clean up a stale lock,
  // so the simplest contract is "the open store starts a reaper".
  const repo = new TabLockRepositoryDexie(db);
  const broadcast = new TabBroadcast();
  const reaper = new LockReaper(repo, {
    staleThresholdMs: DEFAULT_STALE_THRESHOLD_MS,
    reaperIntervalMs: DEFAULT_HEARTBEAT_INTERVAL_MS,
    broadcast,
  });
  reaper.start();
  internals.set(tag, { lock: null, reaper, broadcast });

  broadcast.on((event: TabBroadcastEvent) => {
    const s = get();
    if (s.cpid.length === 0) return;
    if (event.type === 'LOCK_RELEASED' && event.cpid === s.cpid && event.wsUrl === s.wsUrl) {
      if (s.status === 'foreign') {
        void get().refresh();
      }
    }
  });

  return {
    // Stable per-tab id: persisted in sessionStorage so it survives F5/reload
    // in the same tab. That lets CrossTabLock recognise our own previous lock
    // and take it over without waiting ~10 s for the reaper.
    tabId: getOrCreateTabId(),
    cpid: '',
    wsUrl: '',
    status: 'idle',
    holder: null,

    async tryAcquire(cpid: string, wsUrl: string): Promise<{ acquired: boolean }> {
      const { tabId } = get();
      set({ cpid, wsUrl, status: 'acquiring' });

      // Drop any previously-held lock first (changing station identity).
      const current = internals.get(tag);
      if (current?.lock) {
        await current.lock.release();
      }

      const lock = new CrossTabLock(repo, {
        cpid,
        wsUrl,
        tabId,
        broadcast,
      });
      const result = await lock.tryAcquire();
      if (result.acquired) {
        lock.startHeartbeat();
        internals.set(tag, { lock, reaper, broadcast });
        set({ status: 'held', holder: null });
        return { acquired: true };
      }
      internals.set(tag, { lock: null, reaper, broadcast });
      set({ status: 'foreign', holder: result.holder ?? null });
      return { acquired: false };
    },

    async release(): Promise<void> {
      const current = internals.get(tag);
      if (current?.lock) {
        await current.lock.release();
        internals.set(tag, { lock: null, reaper: current.reaper, broadcast: current.broadcast });
      }
      set({ status: 'idle', holder: null });
    },

    async refresh(): Promise<void> {
      const s = get();
      if (s.cpid.length === 0 || s.wsUrl.length === 0) return;
      const holder = await repo.get(s.cpid, s.wsUrl);
      if (!holder) {
        // Lock has been freed — caller (StationLockedPage) is expected to
        // call tryAcquire next.
        set({ status: 'idle', holder: null });
        return;
      }
      if (holder.tabId === s.tabId) {
        set({ status: 'held', holder });
      } else {
        set({ status: 'foreign', holder });
      }
    },
  };
});
